import React, { ReactNode, useCallback, useMemo } from 'react';

import cn from 'classnames';
import styled from '@emotion/styled';

import { useId } from '@britishcouncil/react-common';

export interface ToggleButtonProps<T> {
  active?: boolean;
  className?: string;
  name?: string;
  value: T;
  disabled?: boolean;
  size?: 'lg' | 'md' | 'sm' | 'xs';
  onClick?(value: T): void;
  children?: ReactNode | undefined;
}

export const ToggleButton = <T extends string | number | boolean>({
  className,
  size,
  children,
  name,
  onClick,
  value,
  active,
  disabled,
}: ToggleButtonProps<T>) => {
  const handleClick = useCallback<React.MouseEventHandler>(
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      onClick && onClick(value);
    },
    [onClick, value]
  );
  const id = useId(`${name}-`);
  const combinedClassName = useMemo(
    () =>
      cn(
        'btn',
        size && size !== 'md' ? `btn-${size}` : undefined,
        `btn-${active ? 'primary' : 'default'}`,
        disabled && 'disabled',
        className
      ),
    [active, className, disabled, size]
  );

  return (
    <Label disabled={disabled} htmlFor={id} className={combinedClassName} onClick={handleClick}>
      <input id={id} name={name} type="radio" autoComplete="off" value={`${value}`} />
      {children}
    </Label>
  );
};

const Label = styled('label')<{
  disabled: boolean | undefined;
}>`
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
  border: 3px solid #005cb9;

  &.btn-active {
    background-color: #eee;
    border-color: #000;
    color: #000;
    z-index: 2;
  }

  //mobile styles for toggle buttons
  @media (max-width: 849px) {
    width: 100%;
    margin: 10px 0 0 0 !important;
    border-top-right-radius: 100px !important;
    border-top-left-radius: 100px !important;
    border-bottom-right-radius: 100px !important;
    border-bottom-left-radius: 100px !important;
  }
`;
